<template>
    <div class="deal-card" @click="$emit('open', deal)">
        <div class="card-img-wrap">
            <img v-if="deal.imgUrl" :src="deal.imgUrl" :alt="deal.title || ''" loading="lazy" class="card-img" />
            <div v-else class="card-img-placeholder">no image</div>

            <span v-if="deal.gradeTier" class="grade-badge" :class="`grade-${deal.gradeTier.toLowerCase()}`">
                {{ deal.gradeTier }}
            </span>
            <span v-if="deal.percentOff != null && deal.percentOff > 0" class="pct-off">
                -{{ deal.percentOff }}%
            </span>
        </div>

        <div class="card-body">
            <div class="card-title" :title="deal.title || ''">{{ deal.title || '(untitled)' }}</div>

            <div class="card-prices">
                <span class="deal-price">${{ formatPrice(deal.dealPrice) }}</span>
                <span v-if="hasRetail" class="retail-price">${{ formatPrice(deal.retailPrice!) }}</span>
            </div>

            <div class="card-meta">
                <span v-if="deal.isForMoms" class="for-moms-pill" title="High audience score for moms">🍼 For Moms</span>
                <!-- Pair-model score (admin signal). Only shows if scored.
                     Color-coded so admins can eyeball model agreement at a glance. -->
                <span v-if="deal.pairScore != null" class="pair-score-pill" :class="pairScoreClass"
                      :title="`Pair model audience score: ${deal.pairScore}`">⚖ {{ deal.pairScore }}</span>
                <!-- Pricequal "would I have posted this?" probability. -->
                <span v-if="deal.learnedGreat != null" class="learned-great-pill" :class="learnedGreatClass"
                      :title="`Learned-great probability: ${deal.learnedGreat}% (would the operator have posted this at this price?)`">★ {{ deal.learnedGreat }}</span>
                <span v-if="deal.promoCode" class="promo-chip" :title="`Promo: ${deal.promoCode}`">{{ deal.promoCode }}</span>
                <span v-if="lowStockLabel" class="stock-low">{{ lowStockLabel }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface SearchDeal {
    dealId: number
    title: string | null
    imgUrl: string | null
    dealPrice: number | null
    retailPrice: number | null
    percentOff: number | null
    dealUrl: string | null
    promoCode: string | null
    gradeTier: string | null
    isForMoms: boolean
    categoryPath: string | null
    available: number | null
    // Pair-model output (admin-only on /deals-pairs). null when worker
    // hasn't scored this deal with the pair model yet.
    pairScore: number | null
    // Pricequal model probability (0-100): "would the operator have
    // posted this at this discount?" Surfaced for admin comparison
    // alongside GradeTier. Same null-when-unscored semantics.
    learnedGreat: number | null
}

const props = defineProps<{ deal: SearchDeal }>()
defineEmits<{ (e: 'open', deal: SearchDeal): void }>()

const formatPrice = (n: number | null | undefined) =>
    n != null ? Number(n).toFixed(2) : '—'

const hasRetail = computed(() =>
    props.deal.retailPrice != null
    && props.deal.dealPrice != null
    && props.deal.retailPrice > props.deal.dealPrice
)

const lowStockLabel = computed(() => {
    const a = props.deal.available
    if (a != null && a > 0 && a <= 3) return `Only ${a} left`
    return ''
})

// Color buckets so admin can scan model agreement at a glance --
// 80+ = strong, 60-79 = good, 40-59 = neutral, below 40 = weak.
const pairScoreClass = computed(() => bucketClass(props.deal.pairScore))
const learnedGreatClass = computed(() => bucketClass(props.deal.learnedGreat))
function bucketClass(n: number | null): string {
    if (n == null) return ''
    if (n >= 80) return 'score-strong'
    if (n >= 60) return 'score-good'
    if (n >= 40) return 'score-neutral'
    return 'score-weak'
}
</script>

<style scoped>
.deal-card {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    transition: box-shadow 0.15s, transform 0.05s;
}
.deal-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transform: translateY(-1px);
}

.card-img-wrap {
    position: relative;
    aspect-ratio: 1 / 1;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}
.card-img { max-width: 100%; max-height: 100%; object-fit: contain; }
.card-img-placeholder { color: #aaa; font-size: 12px; }

.grade-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 3px 9px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
}
.grade-great   { background: #2e7d32; color: #fff; }
.grade-good    { background: #66bb6a; color: #fff; }
.grade-sale    { background: #fff3c4; color: #8a6d00; }
.grade-regular { background: #eceff1; color: #555; }

.pct-off {
    position: absolute;
    top: 8px;
    right: 8px;
    background: #d32f2f;
    color: #fff;
    padding: 3px 9px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
}

.card-body {
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
}
.card-title {
    font-size: 13px;
    line-height: 1.35;
    color: #222;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    min-height: 2.7em;
}

.card-prices { display: flex; align-items: baseline; gap: 8px; }
.deal-price { font-size: 18px; font-weight: 700; color: #2e7d32; }
.retail-price { font-size: 13px; color: #999; text-decoration: line-through; }

.card-meta { display: flex; gap: 6px; flex-wrap: wrap; }
.for-moms-pill {
    background: #fce4ec;
    color: #ad1457;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
}
/* Pair-model + pricequal admin pills. Both use 4-bucket color coding
   so admins can scan at a glance which deals the new models love /
   hate vs. the existing tier. */
.pair-score-pill, .learned-great-pill {
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid transparent;
}
.score-strong  { background: #c8e6c9; color: #1b5e20; border-color: #a5d6a7; }
.score-good    { background: #dcedc8; color: #33691e; border-color: #c5e1a5; }
.score-neutral { background: #eceff1; color: #455a64; border-color: #cfd8dc; }
.score-weak    { background: #ffcdd2; color: #b71c1c; border-color: #ef9a9a; }
.promo-chip {
    background: #f3e5f5;
    color: #6a1b9a;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-family: ui-monospace, monospace;
}
.stock-low {
    background: #ffe0b2;
    color: #b45309;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
}
</style>
